import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";

const ViewDocs = () => {
    const { id } = useParams(); // Get the car ID from the URL
    const navigate = useNavigate();
    const [docs, setDocs] = useState([]); // Store document paths (doc1 to doc6)
    const [invoice, setInvoice] = useState(null); // Store invoice path
    const [carDetails, setCarDetails] = useState({}); // Store car details (stock_id, vin, maker, model)
    const [error, setError] = useState(null);
    const [userType, setUserType] = useState("");
    const [userFullName, setUserFullName] = useState("");

    useEffect(() => {
        // Get user data from localStorage
        const storedUserType = localStorage.getItem("usertype");
        const storedUserName = localStorage.getItem("userName");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login"); // Redirect if not logged in
        } else {
            setUserType(storedUserType);
            // Combine first and last name if available
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else if (storedFName) {
                setUserFullName(storedFName);
            } else {
                setUserFullName(storedUserName);
            }
        }
    }, [navigate]);

    useEffect(() => {
        const fetchDocs = async () => {
            try {
                const response = await axios.get(`https://api.saadmotors.com/fetch_car_docs.php?car_id=${id}`);
                // console.log('Docs Response:', response.data); // Debug the response
                if (response.data && response.data.carDetails && Array.isArray(response.data.docs)) {
                    setDocs(response.data.docs.filter(doc => doc)); // Filter out null/empty docs
                    setInvoice(response.data.invoice); // Set invoice path
                    setCarDetails(response.data.carDetails);
                    setError(null);
                } else {
                    console.error('Invalid response format:', response.data);
                    setDocs([]);
                    setInvoice(null);
                    setCarDetails({});
                    setError('Failed to load documents or invoice: Invalid API response');
                }
            } catch (error) {
                console.error('Error fetching documents:', error);
                setDocs([]);
                setInvoice(null);
                setCarDetails({});
                setError('Failed to load documents or invoice: Network or server error');
            }
        };

        fetchDocs();
    }, [id]);

    const handleBack = () => {
        navigate(-1); // Navigate back to the previous page (Vehicles)
    };

    const handleBulkDownload = async () => {
        try {
            const response = await axios.get(`https://api.saadmotors.com/download_docs_zip.php?car_id=${id}`, {
                responseType: 'blob' // Important for handling binary data (ZIP file)
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `vehicle_${id}_docs.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading ZIP:', error);
            alert('Failed to download ZIP file. Please check the console for details.');
        }
    };

    return (
        <div className="admin-container">
            {/* Conditionally Render Sidebar */}
            <div className="sidebar-container">
                {userType === "administrator" && <AdministratorSidebar />}
                {userType === "admin" && <AdminSidebar />}
                {userType === "user" && <UserSidebar />}
            </div>

            <div className="admin-content">
                <div className="adminnavbar">
                    <h1>Welcome to the Admin Panel, {userFullName}</h1>
                    <button onClick={handleBack} className="btn btn-secondary mr-2">
                        Back to Vehicles
                    </button>
                    <button onClick={handleBulkDownload} className="btn btn-primary">
                        Download All Documents (ZIP)
                    </button>
                </div>

                <div className="mainsection">
                    <h3>
                        Vehicle Documents and Invoice (Car ID: {id})
                        {carDetails.stock_id && (
                            <span style={{ fontSize: '1rem', color: '#666', marginLeft: '10px' }}>
                                | Stock ID: {carDetails.stock_id} | VIN: {carDetails.car_id} | {carDetails.maker} {carDetails.model}
                            </span>
                        )}
                    </h3>
                </div>

                <div className="container">
                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    {/* Documents Section */}
                    <h4>Documents</h4>
                    {docs.length > 0 ? (
                        <div className="row">
                            {docs.map((doc, index) => (
                                doc ? (
                                    <div key={index} className="col-md-4 mb-4">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="100"
                                                    height="100"
                                                    fill="currentColor"
                                                    className="bi bi-file-pdf text-danger"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path fillRule="evenodd" d="M14 4.5V14 a2 2 0 0 1-2 2 h-1v-1 h1 a1 1 0 0 0 1-1 V4.5 h-2 A1.5 1.5 0 0 1 9.5 3 V1 H4 a1 1 0 0 0-1 1 v9 H2 V2 a2 2 0 0 1 2-2 h5.5 zM1.6 11.85 H0 v3.999 h.791 v-1.342 h.803 q.43 0 .732-.173 q.305-.175.463-.474 a1.4 1.4 0 0 0 .161-.677 q.0-.375-.158-.677 a1.2 1.2 0 0 0-.46-.477 q-.3-.18 -.732-.179 m.545 1.333 a.8 .8 0 0 1-.085.38 .57 .57 0 0 1-.238.241 .8 .8 0 0 1-.375.082 H.788 V12.48 h.66 q.327 0 .512.181 q.185.183.185.522 m1.217-1.333 v3.999 h1.46 q.602 0 .998-.237 a1.45 1.45 0 0 0 .595-.689 q.196-.45.196-1.084 q.0-.63-.196-1.075 a1.43 1.43 0 0 0-.589-.68 q-.396-.234-1.005-.234 z m.791.645 h.563 q.371 0 .609.152 a.9 .9 0 0 1 .354.454 q.118.302.118.753 a2.3 2.3 0 0 1-.068.592 1.1 1.1 0 0 1-.196.422 .8 .8 0 0 1-.334.252 1.3 1.3 0 0 1-.483.082 h-.563 z m3.743 1.763 v1.591 h-.79 V11.85 h2.548 v.653 H7.896 v1.117 h1.606 v.638 z"/>
                                                </svg>
                                                <p className="card-text mt-2">Document {index + 1}</p>
                                                <button
                                                    className="btn btn-success btn-sm w-100 mt-2"
                                                    onClick={() => {
                                                        window.open(`https://api.saadmotors.com/${doc}`, '_blank');
                                                    }}
                                                >
                                                    View Document
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div key={index} className="col-md-4 mb-4">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <p className="card-text">No PDF available</p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    ) : (
                        <p className="text-center">No documents available for this vehicle.</p>
                    )}

                    {/* Separator */}
                    <hr style={{ margin: '2rem 0', borderTop: '2px solid #ccc' }} />

                    {/* Invoice Section */}
                    <h4>Invoice</h4>
                    {invoice ? (
                        <div className="row">
                            <div className="col-md-4 mb-4">
                                <div className="card text-center">
                                    <div className="card-body">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="100"
                                            height="100"
                                            fill="currentColor"
                                            className="bi bi-file-text text-primary"
                                            viewBox="0 0 16 16"
                                        >
                                            <path d="M5 4 a.5 .5 0 0 0 0 1 h6 a.5 .5 0 0 0 0-1 z m-.5 2.5 A.5 .5 0 0 1 5 6 h6 a.5 .5 0 0 1 0 1 H5 a.5 .5 0 0 1-.5-.5 z M5 8 a.5 .5 0 0 0 0 1 h6 a.5 .5 0 0 0 0-1 z m0 2 a.5 .5 0 0 0 0 1 h3 a.5 .5 0 0 0 0-1 z"/>
                                            <path d="M2 2 a2 2 0 0 1 2-2 h8 a2 2 0 0 1 2 2 v12 a2 2 0 0 1-2 2 H4 a2 2 0 0 1-2-2 z m10-1 H4 a1 1 0 0 0-1 1 v12 a1 1 0 0 0 1 1 h8 a1 1 0 0 0 1-1 V2 a1 1 0 0 0-1-1 z"/>
                                        </svg>
                                        <p className="card-text mt-2">Invoice</p>
                                        <button
                                            className="btn btn-success btn-sm w-100 mt-2"
                                            onClick={() => {
                                                window.open(`https://api.saadmotors.com/${invoice}`, '_blank');
                                            }}
                                        >
                                            View Invoice
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-center">No invoice available for this vehicle.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewDocs;